import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { WEBHOOK_SEARCH_DEMO_BUSINESS, VOICE_OPTIONS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  ArrowLeft,
  Sparkles,
  MapPin,
  Phone,
  CheckCircle2,
  Loader2,
  Globe,
  Search,
  Star,
  Clock,
  Volume2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DemoConfig, GoogleMapsBusinessData } from "@/hooks/use-vapi-demo";
import { AIDisclosureBanner } from "./honest-demo";

// Industry options for the demo
const INDUSTRY_OPTIONS = [
  { id: "hvac", name: "HVAC", description: "Heating, Ventilation, Air Conditioning" },
  { id: "plumbing", name: "Plumbing", description: "Plumbing & Drain Services" },
  { id: "electrical", name: "Electrical", description: "Electrical Services" },
  { id: "roofing", name: "Roofing", description: "Roofing & Gutters" },
  { id: "landscaping", name: "Landscaping", description: "Landscaping & Lawn Care" },
  { id: "auto_repair", name: "Auto Repair", description: "Auto Repair & Mechanics" },
  { id: "dental", name: "Dental", description: "Dental & Medical Clinics" },
  { id: "real_estate", name: "Real Estate", description: "Real Estate Services" },
  { id: "legal", name: "Legal", description: "Legal Services" },
  { id: "accounting", name: "Accounting", description: "Accounting & Financial" },
  { id: "cleaning", name: "Cleaning", description: "Home Cleaning Services" },
  { id: "pest_control", name: "Pest Control", description: "Pest Control Services" },
  { id: "other", name: "Other", description: "Other Service Business" },
];

// Comprehensive list of US and Canadian cities organized by state/province
const LOCATIONS = [
  // California - Southern
  { city: 'Los Angeles', region: 'CA' },
  { city: 'San Diego', region: 'CA' },
  { city: 'Irvine', region: 'CA' },
  { city: 'Anaheim', region: 'CA' },
  { city: 'Long Beach', region: 'CA' },
  { city: 'Riverside', region: 'CA' },
  { city: 'San Bernardino', region: 'CA' },
  { city: 'Ontario', region: 'CA' },
  { city: 'Santa Ana', region: 'CA' },
  { city: 'Corona', region: 'CA' },
  { city: 'Temecula', region: 'CA' },
  { city: 'Palm Springs', region: 'CA' },
  { city: 'Oceanside', region: 'CA' },
  { city: 'Escondido', region: 'CA' },
  { city: 'Pasadena', region: 'CA' },
  { city: 'Glendale', region: 'CA' },
  { city: 'Burbank', region: 'CA' },
  { city: 'Santa Clarita', region: 'CA' },
  { city: 'Bakersfield', region: 'CA' },
  { city: 'Oxnard', region: 'CA' },
  { city: 'Ventura', region: 'CA' },
  { city: 'Thousand Oaks', region: 'CA' },
  { city: 'Santa Barbara', region: 'CA' },
  // California - Northern
  { city: 'San Francisco', region: 'CA' },
  { city: 'Oakland', region: 'CA' },
  { city: 'San Jose', region: 'CA' },
  { city: 'Fremont', region: 'CA' },
  { city: 'Sunnyvale', region: 'CA' },
  { city: 'Santa Clara', region: 'CA' },
  { city: 'Palo Alto', region: 'CA' },
  { city: 'Mountain View', region: 'CA' },
  { city: 'Berkeley', region: 'CA' },
  { city: 'Concord', region: 'CA' },
  { city: 'Stockton', region: 'CA' },
  { city: 'Modesto', region: 'CA' },
  { city: 'Fresno', region: 'CA' },
  { city: 'Sacramento', region: 'CA' },
  { city: 'Roseville', region: 'CA' },
  { city: 'Santa Rosa', region: 'CA' },
  { city: 'Napa', region: 'CA' },
  { city: 'Santa Cruz', region: 'CA' },
  { city: 'Monterey', region: 'CA' },
  // Washington
  { city: 'Seattle', region: 'WA' },
  { city: 'Tacoma', region: 'WA' },
  { city: 'Bellevue', region: 'WA' },
  { city: 'Spokane', region: 'WA' },
  { city: 'Vancouver', region: 'WA' },
  { city: 'Everett', region: 'WA' },
  { city: 'Kent', region: 'WA' },
  { city: 'Renton', region: 'WA' },
  { city: 'Yakima', region: 'WA' },
  { city: 'Bellingham', region: 'WA' },
  { city: 'Olympia', region: 'WA' },
  { city: 'Kirkland', region: 'WA' },
  { city: 'Redmond', region: 'WA' },
  // Oregon
  { city: 'Portland', region: 'OR' },
  { city: 'Salem', region: 'OR' },
  { city: 'Eugene', region: 'OR' },
  { city: 'Gresham', region: 'OR' },
  { city: 'Hillsboro', region: 'OR' },
  { city: 'Beaverton', region: 'OR' },
  { city: 'Bend', region: 'OR' },
  { city: 'Medford', region: 'OR' },
  { city: 'Corvallis', region: 'OR' },
  // Nevada
  { city: 'Las Vegas', region: 'NV' },
  { city: 'Henderson', region: 'NV' },
  { city: 'Reno', region: 'NV' },
  { city: 'Sparks', region: 'NV' },
  { city: 'Carson City', region: 'NV' },
  // Arizona
  { city: 'Phoenix', region: 'AZ' },
  { city: 'Tucson', region: 'AZ' },
  { city: 'Mesa', region: 'AZ' },
  { city: 'Chandler', region: 'AZ' },
  { city: 'Gilbert', region: 'AZ' },
  { city: 'Scottsdale', region: 'AZ' },
  { city: 'Glendale', region: 'AZ' },
  { city: 'Tempe', region: 'AZ' },
  { city: 'Peoria', region: 'AZ' },
  { city: 'Surprise', region: 'AZ' },
  { city: 'Yuma', region: 'AZ' },
  { city: 'Flagstaff', region: 'AZ' },
  { city: 'Sedona', region: 'AZ' },
  // Utah
  { city: 'Salt Lake City', region: 'UT' },
  { city: 'West Valley City', region: 'UT' },
  { city: 'Provo', region: 'UT' },
  { city: 'West Jordan', region: 'UT' },
  { city: 'Orem', region: 'UT' },
  { city: 'Sandy', region: 'UT' },
  { city: 'Ogden', region: 'UT' },
  { city: 'St. George', region: 'UT' },
  { city: 'Park City', region: 'UT' },
  // Colorado
  { city: 'Denver', region: 'CO' },
  { city: 'Colorado Springs', region: 'CO' },
  { city: 'Aurora', region: 'CO' },
  { city: 'Fort Collins', region: 'CO' },
  { city: 'Lakewood', region: 'CO' },
  { city: 'Thornton', region: 'CO' },
  { city: 'Arvada', region: 'CO' },
  { city: 'Westminster', region: 'CO' },
  { city: 'Pueblo', region: 'CO' },
  { city: 'Boulder', region: 'CO' },
  { city: 'Grand Junction', region: 'CO' },
  { city: 'Durango', region: 'CO' },
  { city: 'Aspen', region: 'CO' },
  // Idaho
  { city: 'Boise', region: 'ID' },
  { city: 'Meridian', region: 'ID' },
  { city: 'Nampa', region: 'ID' },
  { city: 'Idaho Falls', region: 'ID' },
  { city: 'Pocatello', region: 'ID' },
  { city: 'Twin Falls', region: 'ID' },
  { city: "Coeur d'Alene", region: 'ID' },
  // Montana
  { city: 'Billings', region: 'MT' },
  { city: 'Missoula', region: 'MT' },
  { city: 'Great Falls', region: 'MT' },
  { city: 'Bozeman', region: 'MT' },
  { city: 'Helena', region: 'MT' },
  // Wyoming
  { city: 'Cheyenne', region: 'WY' },
  { city: 'Casper', region: 'WY' },
  { city: 'Laramie', region: 'WY' },
  { city: 'Jackson', region: 'WY' },
  // New Mexico
  { city: 'Albuquerque', region: 'NM' },
  { city: 'Las Cruces', region: 'NM' },
  { city: 'Rio Rancho', region: 'NM' },
  { city: 'Santa Fe', region: 'NM' },
  // Texas
  { city: 'Houston', region: 'TX' },
  { city: 'San Antonio', region: 'TX' },
  { city: 'Dallas', region: 'TX' },
  { city: 'Austin', region: 'TX' },
  { city: 'Fort Worth', region: 'TX' },
  { city: 'El Paso', region: 'TX' },
  { city: 'Arlington', region: 'TX' },
  { city: 'Corpus Christi', region: 'TX' },
  { city: 'Plano', region: 'TX' },
  { city: 'Laredo', region: 'TX' },
  { city: 'Lubbock', region: 'TX' },
  { city: 'Irving', region: 'TX' },
  { city: 'Amarillo', region: 'TX' },
  { city: 'Frisco', region: 'TX' },
  { city: 'McKinney', region: 'TX' },
  { city: 'Waco', region: 'TX' },
  { city: 'Midland', region: 'TX' },
  { city: 'Odessa', region: 'TX' },
  { city: 'Round Rock', region: 'TX' },
  { city: 'The Woodlands', region: 'TX' },
  { city: 'Sugar Land', region: 'TX' },
  // Illinois
  { city: 'Chicago', region: 'IL' },
  { city: 'Aurora', region: 'IL' },
  { city: 'Naperville', region: 'IL' },
  { city: 'Joliet', region: 'IL' },
  { city: 'Rockford', region: 'IL' },
  { city: 'Elgin', region: 'IL' },
  { city: 'Springfield', region: 'IL' },
  { city: 'Peoria', region: 'IL' },
  { city: 'Champaign', region: 'IL' },
  { city: 'Schaumburg', region: 'IL' },
  // Michigan
  { city: 'Detroit', region: 'MI' },
  { city: 'Grand Rapids', region: 'MI' },
  { city: 'Warren', region: 'MI' },
  { city: 'Sterling Heights', region: 'MI' },
  { city: 'Ann Arbor', region: 'MI' },
  { city: 'Lansing', region: 'MI' },
  { city: 'Flint', region: 'MI' },
  { city: 'Dearborn', region: 'MI' },
  { city: 'Troy', region: 'MI' },
  { city: 'Kalamazoo', region: 'MI' },
  { city: 'Traverse City', region: 'MI' },
  // Ohio
  { city: 'Columbus', region: 'OH' },
  { city: 'Cleveland', region: 'OH' },
  { city: 'Cincinnati', region: 'OH' },
  { city: 'Toledo', region: 'OH' },
  { city: 'Akron', region: 'OH' },
  { city: 'Dayton', region: 'OH' },
  { city: 'Canton', region: 'OH' },
  { city: 'Youngstown', region: 'OH' },
  // Indiana
  { city: 'Indianapolis', region: 'IN' },
  { city: 'Fort Wayne', region: 'IN' },
  { city: 'Evansville', region: 'IN' },
  { city: 'South Bend', region: 'IN' },
  { city: 'Carmel', region: 'IN' },
  { city: 'Fishers', region: 'IN' },
  { city: 'Bloomington', region: 'IN' },
  // Wisconsin
  { city: 'Milwaukee', region: 'WI' },
  { city: 'Madison', region: 'WI' },
  { city: 'Green Bay', region: 'WI' },
  { city: 'Kenosha', region: 'WI' },
  { city: 'Racine', region: 'WI' },
  { city: 'Appleton', region: 'WI' },
  // Minnesota
  { city: 'Minneapolis', region: 'MN' },
  { city: 'St. Paul', region: 'MN' },
  { city: 'Rochester', region: 'MN' },
  { city: 'Bloomington', region: 'MN' },
  { city: 'Duluth', region: 'MN' },
  // Iowa
  { city: 'Des Moines', region: 'IA' },
  { city: 'Cedar Rapids', region: 'IA' },
  { city: 'Davenport', region: 'IA' },
  { city: 'Sioux City', region: 'IA' },
  { city: 'Iowa City', region: 'IA' },
  // Missouri
  { city: 'Kansas City', region: 'MO' },
  { city: 'St. Louis', region: 'MO' },
  { city: 'Springfield', region: 'MO' },
  { city: 'Columbia', region: 'MO' },
  { city: 'Independence', region: 'MO' },
  // Kansas
  { city: 'Wichita', region: 'KS' },
  { city: 'Overland Park', region: 'KS' },
  { city: 'Kansas City', region: 'KS' },
  { city: 'Olathe', region: 'KS' },
  { city: 'Topeka', region: 'KS' },
  // Nebraska
  { city: 'Omaha', region: 'NE' },
  { city: 'Lincoln', region: 'NE' },
  { city: 'Bellevue', region: 'NE' },
  { city: 'Grand Island', region: 'NE' },
  // Florida
  { city: 'Jacksonville', region: 'FL' },
  { city: 'Miami', region: 'FL' },
  { city: 'Tampa', region: 'FL' },
  { city: 'Orlando', region: 'FL' },
  { city: 'St. Petersburg', region: 'FL' },
  { city: 'Hialeah', region: 'FL' },
  { city: 'Port St. Lucie', region: 'FL' },
  { city: 'Cape Coral', region: 'FL' },
  { city: 'Tallahassee', region: 'FL' },
  { city: 'Fort Lauderdale', region: 'FL' },
  { city: 'Pembroke Pines', region: 'FL' },
  { city: 'Hollywood', region: 'FL' },
  { city: 'Gainesville', region: 'FL' },
  { city: 'Coral Springs', region: 'FL' },
  { city: 'Clearwater', region: 'FL' },
  { city: 'West Palm Beach', region: 'FL' },
  { city: 'Lakeland', region: 'FL' },
  { city: 'Boca Raton', region: 'FL' },
  { city: 'Fort Myers', region: 'FL' },
  { city: 'Sarasota', region: 'FL' },
  { city: 'Pensacola', region: 'FL' },
  { city: 'Naples', region: 'FL' },
  { city: 'Daytona Beach', region: 'FL' },
  // Georgia
  { city: 'Atlanta', region: 'GA' },
  { city: 'Augusta', region: 'GA' },
  { city: 'Columbus', region: 'GA' },
  { city: 'Macon', region: 'GA' },
  { city: 'Savannah', region: 'GA' },
  { city: 'Athens', region: 'GA' },
  { city: 'Sandy Springs', region: 'GA' },
  { city: 'Roswell', region: 'GA' },
  { city: 'Marietta', region: 'GA' },
  { city: 'Alpharetta', region: 'GA' },
  // North Carolina
  { city: 'Charlotte', region: 'NC' },
  { city: 'Raleigh', region: 'NC' },
  { city: 'Greensboro', region: 'NC' },
  { city: 'Durham', region: 'NC' },
  { city: 'Winston-Salem', region: 'NC' },
  { city: 'Fayetteville', region: 'NC' },
  { city: 'Cary', region: 'NC' },
  { city: 'Wilmington', region: 'NC' },
  { city: 'High Point', region: 'NC' },
  { city: 'Asheville', region: 'NC' },
  // South Carolina
  { city: 'Charleston', region: 'SC' },
  { city: 'Columbia', region: 'SC' },
  { city: 'North Charleston', region: 'SC' },
  { city: 'Mount Pleasant', region: 'SC' },
  { city: 'Rock Hill', region: 'SC' },
  { city: 'Greenville', region: 'SC' },
  { city: 'Myrtle Beach', region: 'SC' },
  // Virginia
  { city: 'Virginia Beach', region: 'VA' },
  { city: 'Norfolk', region: 'VA' },
  { city: 'Chesapeake', region: 'VA' },
  { city: 'Richmond', region: 'VA' },
  { city: 'Newport News', region: 'VA' },
  { city: 'Alexandria', region: 'VA' },
  { city: 'Hampton', region: 'VA' },
  { city: 'Roanoke', region: 'VA' },
  { city: 'Lynchburg', region: 'VA' },
  { city: 'Charlottesville', region: 'VA' },
  // Tennessee
  { city: 'Nashville', region: 'TN' },
  { city: 'Memphis', region: 'TN' },
  { city: 'Knoxville', region: 'TN' },
  { city: 'Chattanooga', region: 'TN' },
  { city: 'Clarksville', region: 'TN' },
  { city: 'Murfreesboro', region: 'TN' },
  { city: 'Franklin', region: 'TN' },
  // Kentucky
  { city: 'Louisville', region: 'KY' },
  { city: 'Lexington', region: 'KY' },
  { city: 'Bowling Green', region: 'KY' },
  { city: 'Owensboro', region: 'KY' },
  // Alabama
  { city: 'Birmingham', region: 'AL' },
  { city: 'Montgomery', region: 'AL' },
  { city: 'Huntsville', region: 'AL' },
  { city: 'Mobile', region: 'AL' },
  { city: 'Tuscaloosa', region: 'AL' },
  // Mississippi
  { city: 'Jackson', region: 'MS' },
  { city: 'Gulfport', region: 'MS' },
  { city: 'Southaven', region: 'MS' },
  { city: 'Hattiesburg', region: 'MS' },
  { city: 'Biloxi', region: 'MS' },
  // Louisiana
  { city: 'New Orleans', region: 'LA' },
  { city: 'Baton Rouge', region: 'LA' },
  { city: 'Shreveport', region: 'LA' },
  { city: 'Lafayette', region: 'LA' },
  { city: 'Lake Charles', region: 'LA' },
  // Arkansas
  { city: 'Little Rock', region: 'AR' },
  { city: 'Fort Smith', region: 'AR' },
  { city: 'Fayetteville', region: 'AR' },
  { city: 'Springdale', region: 'AR' },
  { city: 'Jonesboro', region: 'AR' },
  // Oklahoma
  { city: 'Oklahoma City', region: 'OK' },
  { city: 'Tulsa', region: 'OK' },
  { city: 'Norman', region: 'OK' },
  { city: 'Broken Arrow', region: 'OK' },
  { city: 'Edmond', region: 'OK' },
  // New York
  { city: 'New York City', region: 'NY' },
  { city: 'Buffalo', region: 'NY' },
  { city: 'Rochester', region: 'NY' },
  { city: 'Yonkers', region: 'NY' },
  { city: 'Syracuse', region: 'NY' },
  { city: 'Albany', region: 'NY' },
  { city: 'Long Island', region: 'NY' },
  { city: 'White Plains', region: 'NY' },
  // Pennsylvania
  { city: 'Philadelphia', region: 'PA' },
  { city: 'Pittsburgh', region: 'PA' },
  { city: 'Allentown', region: 'PA' },
  { city: 'Reading', region: 'PA' },
  { city: 'Scranton', region: 'PA' },
  { city: 'Bethlehem', region: 'PA' },
  { city: 'Lancaster', region: 'PA' },
  { city: 'Harrisburg', region: 'PA' },
  { city: 'Erie', region: 'PA' },
  // New Jersey
  { city: 'Newark', region: 'NJ' },
  { city: 'Jersey City', region: 'NJ' },
  { city: 'Paterson', region: 'NJ' },
  { city: 'Elizabeth', region: 'NJ' },
  { city: 'Edison', region: 'NJ' },
  { city: 'Trenton', region: 'NJ' },
  { city: 'Camden', region: 'NJ' },
  { city: 'Cherry Hill', region: 'NJ' },
  { city: 'Atlantic City', region: 'NJ' },
  { city: 'Princeton', region: 'NJ' },
  // Massachusetts
  { city: 'Boston', region: 'MA' },
  { city: 'Worcester', region: 'MA' },
  { city: 'Springfield', region: 'MA' },
  { city: 'Lowell', region: 'MA' },
  { city: 'Cambridge', region: 'MA' },
  { city: 'New Bedford', region: 'MA' },
  { city: 'Brockton', region: 'MA' },
  { city: 'Quincy', region: 'MA' },
  { city: 'Plymouth', region: 'MA' },
  // Connecticut
  { city: 'Bridgeport', region: 'CT' },
  { city: 'New Haven', region: 'CT' },
  { city: 'Stamford', region: 'CT' },
  { city: 'Hartford', region: 'CT' },
  { city: 'Waterbury', region: 'CT' },
  { city: 'Norwalk', region: 'CT' },
  // Rhode Island
  { city: 'Providence', region: 'RI' },
  { city: 'Warwick', region: 'RI' },
  { city: 'Cranston', region: 'RI' },
  { city: 'Pawtucket', region: 'RI' },
  { city: 'Newport', region: 'RI' },
  // Maine
  { city: 'Portland', region: 'ME' },
  { city: 'Lewiston', region: 'ME' },
  { city: 'Bangor', region: 'ME' },
  { city: 'Augusta', region: 'ME' },
  // New Hampshire
  { city: 'Manchester', region: 'NH' },
  { city: 'Nashua', region: 'NH' },
  { city: 'Concord', region: 'NH' },
  { city: 'Portsmouth', region: 'NH' },
  // Vermont
  { city: 'Burlington', region: 'VT' },
  { city: 'South Burlington', region: 'VT' },
  { city: 'Rutland', region: 'VT' },
  { city: 'Montpelier', region: 'VT' },
  // Delaware
  { city: 'Wilmington', region: 'DE' },
  { city: 'Dover', region: 'DE' },
  { city: 'Newark', region: 'DE' },
  // Maryland
  { city: 'Baltimore', region: 'MD' },
  { city: 'Frederick', region: 'MD' },
  { city: 'Rockville', region: 'MD' },
  { city: 'Gaithersburg', region: 'MD' },
  { city: 'Bowie', region: 'MD' },
  { city: 'Annapolis', region: 'MD' },
  // Washington DC
  { city: 'Washington', region: 'DC' },
  // West Virginia
  { city: 'Charleston', region: 'WV' },
  { city: 'Huntington', region: 'WV' },
  { city: 'Morgantown', region: 'WV' },
  // Alaska
  { city: 'Anchorage', region: 'AK' },
  { city: 'Fairbanks', region: 'AK' },
  { city: 'Juneau', region: 'AK' },
  // Hawaii
  { city: 'Honolulu', region: 'HI' },
  { city: 'Pearl City', region: 'HI' },
  { city: 'Hilo', region: 'HI' },
  { city: 'Kailua', region: 'HI' },
  { city: 'Maui', region: 'HI' },
  // North Dakota
  { city: 'Fargo', region: 'ND' },
  { city: 'Bismarck', region: 'ND' },
  { city: 'Grand Forks', region: 'ND' },
  // South Dakota
  { city: 'Sioux Falls', region: 'SD' },
  { city: 'Rapid City', region: 'SD' },
  { city: 'Aberdeen', region: 'SD' },
  // === CANADA ===
  // British Columbia
  { city: 'Vancouver', region: 'BC' },
  { city: 'Surrey', region: 'BC' },
  { city: 'Burnaby', region: 'BC' },
  { city: 'Richmond', region: 'BC' },
  { city: 'Coquitlam', region: 'BC' },
  { city: 'Port Coquitlam', region: 'BC' },
  { city: 'Port Moody', region: 'BC' },
  { city: 'Langley', region: 'BC' },
  { city: 'Abbotsford', region: 'BC' },
  { city: 'Kelowna', region: 'BC' },
  { city: 'Victoria', region: 'BC' },
  { city: 'Nanaimo', region: 'BC' },
  { city: 'Kamloops', region: 'BC' },
  { city: 'Chilliwack', region: 'BC' },
  { city: 'Pitt Meadows', region: 'BC' },
  { city: 'Maple Ridge', region: 'BC' },
  { city: 'New Westminster', region: 'BC' },
  { city: 'North Vancouver', region: 'BC' },
  { city: 'West Vancouver', region: 'BC' },
  { city: 'White Rock', region: 'BC' },
  { city: 'Prince George', region: 'BC' },
  // Alberta
  { city: 'Calgary', region: 'AB' },
  { city: 'Edmonton', region: 'AB' },
  { city: 'Red Deer', region: 'AB' },
  { city: 'Lethbridge', region: 'AB' },
  { city: 'St. Albert', region: 'AB' },
  { city: 'Medicine Hat', region: 'AB' },
  { city: 'Grande Prairie', region: 'AB' },
  { city: 'Airdrie', region: 'AB' },
  { city: 'Spruce Grove', region: 'AB' },
  { city: 'Sherwood Park', region: 'AB' },
  // Ontario
  { city: 'Toronto', region: 'ON' },
  { city: 'Ottawa', region: 'ON' },
  { city: 'Mississauga', region: 'ON' },
  { city: 'Brampton', region: 'ON' },
  { city: 'Hamilton', region: 'ON' },
  { city: 'London', region: 'ON' },
  { city: 'Markham', region: 'ON' },
  { city: 'Vaughan', region: 'ON' },
  { city: 'Kitchener', region: 'ON' },
  { city: 'Windsor', region: 'ON' },
  { city: 'Richmond Hill', region: 'ON' },
  { city: 'Oakville', region: 'ON' },
  { city: 'Burlington', region: 'ON' },
  { city: 'Oshawa', region: 'ON' },
  { city: 'Barrie', region: 'ON' },
  { city: 'St. Catharines', region: 'ON' },
  { city: 'Cambridge', region: 'ON' },
  { city: 'Kingston', region: 'ON' },
  { city: 'Guelph', region: 'ON' },
  { city: 'Thunder Bay', region: 'ON' },
  { city: 'Waterloo', region: 'ON' },
  { city: 'Sudbury', region: 'ON' },
  // Quebec
  { city: 'Montreal', region: 'QC' },
  { city: 'Quebec City', region: 'QC' },
  { city: 'Laval', region: 'QC' },
  { city: 'Gatineau', region: 'QC' },
  { city: 'Longueuil', region: 'QC' },
  { city: 'Sherbrooke', region: 'QC' },
  { city: 'Trois-Rivières', region: 'QC' },
  { city: 'Lévis', region: 'QC' },
  { city: 'Terrebonne', region: 'QC' },
  { city: 'Saint-Jean-sur-Richelieu', region: 'QC' },
  // Manitoba
  { city: 'Winnipeg', region: 'MB' },
  { city: 'Brandon', region: 'MB' },
  { city: 'Steinbach', region: 'MB' },
  // Saskatchewan
  { city: 'Saskatoon', region: 'SK' },
  { city: 'Regina', region: 'SK' },
  { city: 'Prince Albert', region: 'SK' },
  { city: 'Moose Jaw', region: 'SK' },
  // Nova Scotia
  { city: 'Halifax', region: 'NS' },
  { city: 'Dartmouth', region: 'NS' },
  { city: 'Sydney', region: 'NS' },
  // New Brunswick
  { city: 'Moncton', region: 'NB' },
  { city: 'Saint John', region: 'NB' },
  { city: 'Fredericton', region: 'NB' },
  // Newfoundland and Labrador
  { city: "St. John's", region: 'NL' },
  { city: 'Mount Pearl', region: 'NL' },
  { city: 'Corner Brook', region: 'NL' },
  // Prince Edward Island
  { city: 'Charlottetown', region: 'PE' },
  { city: 'Summerside', region: 'PE' },
];

interface DemoConfigFormProps {
  onSubmit: (config: DemoConfig) => void;
  onBack: () => void;
}

export function DemoConfigForm({ onSubmit, onBack }: DemoConfigFormProps) {
  const [businessName, setBusinessName] = useState("");
  const [website, setWebsite] = useState("");
  const [city, setCity] = useState("");
  const [industry, setIndustry] = useState("hvac"); // Default to HVAC
  const [voiceId, setVoiceId] = useState("EXAVITQu4vr4xnSDxMaL"); // Default to Sarah
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<GoogleMapsBusinessData | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Filter locations based on input
  const filteredLocations = city.trim()
    ? LOCATIONS.filter(loc => {
        const searchTerm = city.toLowerCase();
        const fullLocation = `${loc.city}, ${loc.region}`.toLowerCase();
        const cityOnly = loc.city.toLowerCase();
        return cityOnly.includes(searchTerm) || fullLocation.includes(searchTerm);
      }).slice(0, 8)
    : [];

  // Handle click outside to close suggestions
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || filteredLocations.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < filteredLocations.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      const selected = filteredLocations[highlightedIndex];
      setCity(`${selected.city}, ${selected.region}`);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  };

  const handleLocationSelect = (loc: { city: string; region: string }) => {
    setCity(`${loc.city}, ${loc.region}`);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  // Field-level validation
  const errors = {
    businessName: businessName.trim().length < 2 ? "Business name is required (min 2 characters)" : "",
    city: city.trim().length < 2 ? "City is required" : "",
  };
  const isValid = !errors.businessName && !errors.city;
  const showError = (field: keyof typeof errors) =>
    (touched[field] || submitAttempted) && errors[field];

  // Search for business on Google Maps
  const searchBusiness = async () => {
    if (!isValid) return;

    setIsSearching(true);
    setSearchError(null);
    setSearchResult(null);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(WEBHOOK_SEARCH_DEMO_BUSINESS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: businessName.trim(),
          city: city.trim(),
          website: website.trim() || undefined,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to search for business');
      }

      const text = await response.text();
      if (!text || text.trim() === '') {
        throw new Error('Search returned empty response');
      }

      const data = JSON.parse(text);

      if (data.found && data.business) {
        setSearchResult(data.business);
      } else {
        // Business not found, but we can still proceed with manual data
        setSearchResult(null);
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Business search error:', error);
      const message = error instanceof DOMException && error.name === 'AbortError'
        ? 'Business search timed out. You can still proceed with the demo.'
        : 'Could not find business on Google Maps. You can still proceed with the demo.';
      setSearchError(message);
      setSearchResult(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = () => {
    if (!isValid) return;

    onSubmit({
      businessName: businessName.trim(),
      city: city.trim(),
      website: website.trim() || undefined,
      industry: industry,
      demoMode: "inbound",
      googleMapsData: searchResult || undefined,
      voiceId: voiceId,
    });
  };

  // Auto-search when business name and city are filled
  const handleSearchAndSubmit = async () => {
    setSubmitAttempted(true);
    if (!isValid) return;

    await searchBusiness();
    // After search completes, submit regardless of result
    handleSubmit();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      {/* AI Disclosure Banner */}
      <AIDisclosureBanner />

      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-lime-500/10 border border-lime-500/30 px-3 py-1.5 rounded-sm mb-4">
          <Sparkles className="w-3.5 h-3.5 text-lime-400" />
          <span className="text-xs font-semibold text-lime-400 uppercase tracking-wider">Step 2 of 3</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-gray-100 mb-3 tracking-tight">
          Tell Us About Your Business
        </h1>
        <p className="text-gray-400">
          We'll find your business on Google and personalize the AI receptionist for you
        </p>
      </div>

      <div className="bg-gradient-to-br from-[#2e2e2e] via-[#2d2d2d] to-[#2c2c2c] border border-white/[0.1] p-8 space-y-8">
        {/* Demo Type Header */}
        <div className="flex items-center gap-4 p-4 bg-[#333333] border border-white/[0.08]">
          <div className="w-12 h-12 bg-gradient-to-br from-[#f4f4f4] to-lime-500 flex items-center justify-center shrink-0">
            <Phone className="w-6 h-6 text-[#292929]" />
          </div>
          <div>
            <div className="font-semibold text-gray-100 flex items-center gap-2">
              Inbound Call Demo
              <CheckCircle2 className="w-4 h-4 text-lime-400" />
            </div>
            <div className="text-sm text-gray-400">
              Experience how customers call and book appointments with your AI receptionist
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="space-y-5">
          <Label className="text-base font-semibold text-gray-100">Business Information</Label>

          {/* Business Name */}
          <div className="space-y-2">
            <Label htmlFor="businessName" className="flex items-center gap-2 text-gray-100">
              <Building2 className="w-4 h-4 text-lime-400/70" />
              Business Name <span className="text-amber-500">*</span>
            </Label>
            <Input
              id="businessName"
              placeholder="e.g., Elite HVAC Solutions"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, businessName: true }))}
              aria-invalid={!!showError("businessName")}
              aria-describedby={showError("businessName") ? "businessName-error" : "businessName-hint"}
              required
              className={cn(
                "bg-[#333333] border-white/[0.08] text-gray-100 placeholder:text-gray-500 focus:border-lime-500/50 focus-visible:ring-lime-500/50",
                showError("businessName") && "border-red-500/50 focus:border-red-500/50 focus-visible:ring-red-500/50"
              )}
              maxLength={60}
            />
            {showError("businessName") ? (
              <p id="businessName-error" role="alert" className="text-xs text-red-400">{errors.businessName}</p>
            ) : (
              <p id="businessName-hint" className="text-xs text-gray-500">Enter your business name exactly as it appears on Google</p>
            )}
          </div>

          {/* City with Autocomplete */}
          <div className="space-y-2 relative">
            <Label htmlFor="city" className="flex items-center gap-2 text-gray-100">
              <MapPin className="w-4 h-4 text-lime-400/70" />
              City <span className="text-amber-500">*</span>
            </Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                ref={inputRef}
                id="city"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showSuggestions && filteredLocations.length > 0}
                aria-controls="city-suggestions"
                aria-activedescendant={highlightedIndex >= 0 ? `city-option-${highlightedIndex}` : undefined}
                placeholder="Start typing your city..."
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setShowSuggestions(true);
                  setHighlightedIndex(-1);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTouched(t => ({ ...t, city: true }))}
                onKeyDown={handleKeyDown}
                aria-invalid={!!showError("city")}
                aria-describedby={showError("city") ? "city-error" : undefined}
                required
                className={cn(
                  "pl-10 bg-[#333333] border-white/[0.08] text-gray-100 placeholder:text-gray-500 focus:border-lime-500/50 focus-visible:ring-lime-500/50",
                  showError("city") && "border-red-500/50 focus:border-red-500/50 focus-visible:ring-red-500/50"
                )}
              />
            </div>

            {/* City Suggestions Dropdown */}
            {showSuggestions && filteredLocations.length > 0 && (
              <div
                ref={suggestionsRef}
                id="city-suggestions"
                role="listbox"
                aria-label="City suggestions"
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#333333] border border-white/[0.1] rounded-md shadow-lg max-h-[240px] overflow-auto"
              >
                {filteredLocations.map((loc, index) => (
                  <button
                    key={`${loc.city}-${loc.region}`}
                    id={`city-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={highlightedIndex === index}
                    onClick={() => handleLocationSelect(loc)}
                    className={cn(
                      'w-full px-3 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-white/[0.05] transition-colors',
                      highlightedIndex === index && 'bg-lime-500/10'
                    )}
                  >
                    <MapPin className="h-3.5 w-3.5 text-lime-300 shrink-0" />
                    <span className="text-gray-100">{loc.city}</span>
                    <span className="text-gray-500">{loc.region}</span>
                  </button>
                ))}
              </div>
            )}
            {showError("city") && (
              <p id="city-error" role="alert" className="text-xs text-red-400">{errors.city}</p>
            )}
          </div>

          {/* Industry Selection */}
          <div className="space-y-2">
            <Label htmlFor="industry" className="flex items-center gap-2 text-gray-100">
              <Building2 className="w-4 h-4 text-lime-400/70" />
              Industry <span className="text-amber-500">*</span>
            </Label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger className="bg-[#333333] border-white/[0.08] text-gray-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#333333] border-white/[0.08] max-h-[300px]">
                {INDUSTRY_OPTIONS.map((ind) => (
                  <SelectItem key={ind.id} value={ind.id}>
                    <span className="flex items-center gap-2">
                      <span>{ind.name}</span>
                      <span className="text-xs text-gray-500">({ind.description})</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Select your industry so we can show relevant capabilities</p>
          </div>

          {/* Website (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="website" className="flex items-center gap-2 text-gray-100">
              <Globe className="w-4 h-4 text-lime-400/70" />
              Website <span className="text-gray-500 text-xs">(optional)</span>
            </Label>
            <Input
              id="website"
              placeholder="e.g., www.yourbusiness.com"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="bg-[#333333] border-white/[0.08] text-gray-100 placeholder:text-gray-500 focus:border-lime-500/50 focus-visible:ring-lime-500/50"
            />
            <p className="text-xs text-gray-500">If provided, helps us find the right business</p>
          </div>

          {/* Voice Selection */}
          <div className="space-y-2">
            <Label htmlFor="voiceId" className="flex items-center gap-2 text-gray-100">
              <Volume2 className="w-4 h-4 text-lime-300/70" />
              AI Voice
            </Label>
            <Select value={voiceId} onValueChange={setVoiceId}>
              <SelectTrigger className="bg-[#333333] border-white/[0.08] text-gray-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#333333] border-white/[0.08]">
                {VOICE_OPTIONS.map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    <span className="flex items-center gap-2">
                      <span>{voice.name}</span>
                      <span className="text-xs text-gray-500">({voice.description})</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Choose the voice for your AI receptionist demo</p>
          </div>
        </div>

        {/* Search Result Preview (if found) */}
        {searchResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-[#333333] border border-white/[0.08] rounded-sm space-y-3"
          >
            <div className="flex items-center gap-2 text-lime-400 font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              Business Found on Google
            </div>
            <div className="grid gap-2 text-sm">
              {searchResult.address && (
                <div className="flex items-center gap-2 text-gray-100">
                  <MapPin className="w-3.5 h-3.5 text-gray-500" />
                  {searchResult.address}
                </div>
              )}
              {searchResult.phone && (
                <div className="flex items-center gap-2 text-gray-100">
                  <Phone className="w-3.5 h-3.5 text-gray-500" />
                  {searchResult.phone}
                </div>
              )}
              {searchResult.rating && (
                <div className="flex items-center gap-2 text-gray-100">
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  {searchResult.rating}{searchResult.reviews_count != null ? ` (${searchResult.reviews_count.toLocaleString()} reviews)` : ''}
                </div>
              )}
              {searchResult.hours_of_operation && searchResult.hours_of_operation.length > 0 && (
                <div className="flex items-start gap-2 text-gray-100">
                  <Clock className="w-3.5 h-3.5 text-gray-500 mt-0.5" />
                  <span className="text-xs">{searchResult.hours_of_operation[0]}</span>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Search Error */}
        {searchError && (
          <div className="p-3 bg-[#333333] border border-white/[0.08] rounded-sm">
            <p className="text-sm text-amber-500">{searchError}</p>
          </div>
        )}

        {/* How It Works */}
        <div className="p-4 bg-[#333333] border border-white/[0.08] rounded-sm">
          <div className="flex items-center gap-2 text-gray-100 font-medium mb-3">
            <Search className="w-4 h-4 text-lime-400" />
            How It Works
          </div>
          <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
            <li>We search Google Maps for your business</li>
            <li>Extract your hours, address, and services</li>
            <li>Customize the AI receptionist with your info</li>
            <li>You'll be connected in seconds</li>
          </ol>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Button
            variant="outline"
            onClick={onBack}
            className="gap-2 border-white/[0.15] hover:bg-white/[0.05] text-gray-400"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button
            onClick={handleSearchAndSubmit}
            disabled={!isValid || isSearching}
            className="flex-1 gap-2 bg-gradient-to-r from-[#f4f4f4] to-lime-400 text-[#292929] font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-lime-500/20"
          >
            {isSearching ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Finding Your Business...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Start Voice Demo
              </>
            )}
          </Button>
        </div>

        <p className="text-xs text-gray-500 text-center">
          Demo lasts up to 5 minutes. Microphone access required.
        </p>
      </div>
    </motion.div>
  );
}
